import { Badge } from '@components/badge';
import { HideContainer } from '@components/hide-container';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconCloseSmall } from '@components/icons/close-small';
import { getComparatorLabel } from '@components/integrations';
import { Spinner } from '@components/loading-spinner';
import { Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useEmailTriggerLog } from 'api/email-triggers/hooks';
import { ConditionResult } from 'api/email-triggers/requests';

export const usePreviewEmailTriggerLog = () => {
  return useModal(PreviewEmailTriggerLog, { variant: 'LARGE' });
};

type PreviewEmailTriggerLogProps = {
  triggerShortId: string;
  logShortId: string;
};

export const PreviewEmailTriggerLog = ({
  triggerShortId,
  logShortId,
}: PreviewEmailTriggerLogProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: log, isLoading } = useEmailTriggerLog(triggerShortId, logShortId);

  if (!log || isLoading) {
    return <Spinner />;
  }

  const isError = Boolean(log.errorMessage);
  const isConditionsError = log.errorMessage === 'Conditions not met';

  return (
    <Modal>
      <Modal.SimpleHeader title={abbreviateDate(log.triggeredOnDate, 'en')} />
      <Modal.Content>
        <Stack space={4}>
          <Stack space={1}>
            <Text font="semibold14" text={`${t('sender')}: ${log.sender}`} />
            <Text font="semibold14" text={`${t('subject')}: ${log.subject}`} />
          </Stack>

          {log.bodyPlain && (
            <HideContainer title={t('emailBody')} defaultClosed>
              <Text
                font="regular14"
                color="textSecondary"
                text={log.bodyPlain}
                style={{ whiteSpace: 'pre-wrap' }}
              />
            </HideContainer>
          )}

          <Separator />

          <div>
            <Badge
              size="md"
              icon={isError ? <IconCloseSmall /> : <IconCheckmarkSmall />}
              variant={isError ? 'red' : 'green'}
              text={isError ? log.errorMessage : t('success')}
            />
          </div>
          {isError && <Text font="regular14" text={t('actionNotTriggered')} />}

          {isConditionsError && log.conditionResults.length > 0 && (
            <>
              <Separator />
              <Stack space={3}>
                <Text font="bold14" text={t('conditionResults')} />
                {log.conditionResults.map((result, index) => {
                  return <ConditionResultRow key={index} result={result} />;
                })}
              </Stack>
            </>
          )}

          {log.extractedFieldsDisplay.length > 0 && (
            <>
              <Separator />
              <Stack space={3}>
                <Text font="bold14" text={t('extractedFields')} />
                {log.extractedFieldsDisplay.map((field) => {
                  return (
                    <Stack key={field.label} orientation="horizontal" justify="space-between">
                      <Text font="semibold14" text={field.label} />
                      <Text font="regular14" color="textSecondary" text={field.value || '—'} />
                    </Stack>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const ConditionResultRow = ({ result }: { result: ConditionResult }) => {
  const { t } = useScopedTranslate(locale);
  const comparatorLabel = getComparatorLabel(result.comparator);

  return (
    <Stack space={1}>
      <Stack orientation="horizontal" align="center" space={2}>
        {result.success ? (
          <IconCheckmark color="elementGreen100" size={16} />
        ) : (
          <IconCloseRegular color="elementRed100" size={16} />
        )}
        <Text font="semibold14" text={`${result.field} ${comparatorLabel} ${result.value}`} />
      </Stack>
      <Text
        font="regular12"
        color="textSecondary"
        text={`${t('receivedValue')}: ${result.receivedValue ?? '—'}`}
        style={{ marginLeft: 24 }}
      />
    </Stack>
  );
};

export const locale = {
  en: {
    sender: 'From',
    subject: 'Subject',
    emailBody: 'Email body',
    extractedFields: 'Extracted fields',
    conditionResults: 'Condition results',
    receivedValue: 'Received',
    success: 'Traveller created successfully',
    actionNotTriggered: 'The traveller was not created',
  },
};
